import { api } from './api';

export interface ReviewData {
  id: string;
  bookingId: string;
  providerId: string;
  caseId: string;
  rating: number;
  comment?: string;
  isPublic: boolean;
  createdAt: Date;
  updatedAt: Date;
  booking?: {
    id: string;
    status: string;
    createdAt: Date;
  };
  serviceProvider?: {
    id: string;
    companyName: string;
  };
}

export interface CreateReviewData {
  rating: number;
  comment?: string;
}

export interface ReviewListResponse {
  reviews: ReviewData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class ReviewService {
  /**
   * Create a new review for a booking
   */
  async createReview(bookingId: string, reviewData: CreateReviewData): Promise<ReviewData> {
    const response = await api.post(`/reviews?bookingId=${bookingId}`, reviewData);
    return response.data.data;
  }

  /**
   * Get review for a specific booking
   */
  async getReviewByBookingId(bookingId: string): Promise<ReviewData | null> {
    try {
      const response = await api.get(`/reviews/booking/${bookingId}`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get review (alias for getReviewByBookingId)
   */
  async getReview(bookingId: string, serviceType?: string): Promise<ReviewData | null> {
    return this.getReviewByBookingId(bookingId);
  }

  /**
   * Get all reviews for a provider
   */
  async getProviderReviews(
    providerId: string,
    options?: {
      page?: number;
      limit?: number;
    }
  ): Promise<ReviewListResponse> {
    const params = new URLSearchParams();

    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString();
    const url = `/reviews/provider/${providerId}${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data.data;
  }

  /**
   * Check if a booking is eligible for review (completed and not already reviewed)
   */
  async checkReviewEligibility(bookingId: string): Promise<boolean> {
    try {
      const review = await this.getReviewByBookingId(bookingId);
      return review === null; // Eligible if no review exists yet
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      return false;
    }
  }
}

export const reviewService = new ReviewService();